import { ReactNode } from "react"
import { Clock } from "lucide-react"
import type { TFunction } from "i18next"
import { Trans, useTranslation } from "react-i18next"
import { cn } from "@/lib/utils"
import { Badge } from "@/components/ui/badge"
import { Progress } from "@/components/ui/progress"
import { formatHoursMinutes } from "@/lib/i18n/format"
import {
  DriverWorkTime,
  DrivingLimit,
  Resting,
  TodayFigures,
  WeeklyRest,
} from "@/lib/workTime"

const Figure = ({
  value,
  label,
  align = "left",
}: {
  value: string
  label: string
  align?: "left" | "right"
}) => (
  <div
    className={cn("flex flex-col gap-0.5", align === "right" && "items-end")}
  >
    <span className="text-base font-semibold tabular-nums tracking-tight text-foreground">
      {value}
    </span>
    <span className="text-xs text-muted-foreground">{label}</span>
  </div>
)

const Section = ({
  title,
  aside,
  testId,
  children,
}: {
  title: string
  aside?: ReactNode
  testId: string
  children: ReactNode
}) => (
  <section
    data-testid={testId}
    className="flex flex-col gap-2 rounded-lg border border-border p-3"
  >
    <div className="flex items-center justify-between">
      <h3 className="text-xs font-medium text-muted-foreground">{title}</h3>
      {aside}
    </div>
    {children}
  </section>
)

const Meter = ({
  share,
  caption,
  marker,
}: {
  share: number | null
  caption: string
  marker?: { share: number; label: string }
}) => (
  <div className="flex flex-col gap-1">
    {share !== null && (
      <div className="relative">
        <Progress value={share * 100} className="h-2.5" />
        {marker && (
          <span
            className="absolute -top-[3px] h-4 w-0.5 -translate-x-1/2 rounded-full bg-amber-500 ring-2 ring-card"
            style={{ left: `${marker.share * 100}%` }}
            title={marker.label}
          />
        )}
      </div>
    )}
    <span className="text-xs text-muted-foreground">{caption}</span>
  </div>
)

const InfoRow = ({
  children,
  tone = "muted",
}: {
  children: ReactNode
  tone?: "muted" | "warning"
}) => (
  <div
    className={cn(
      "text-sm",
      tone === "warning"
        ? "font-medium text-amber-700 dark:text-amber-400"
        : "text-muted-foreground",
    )}
  >
    {children}
  </div>
)

const Emphasis = ({ children }: { children?: ReactNode }) => (
  <span className="font-medium text-foreground">{children}</span>
)

// Same "4h 05m" as formatDuration in lib/warsawTime (negative clamps to 0),
// with the units translated in Polish.
const formatDuration = (t: TFunction, seconds: number): string =>
  formatHoursMinutes(t, Math.max(0, seconds))

type Period = "week" | "twoWeeks"

const LimitSection = ({
  title,
  testId,
  limit,
  period,
  children,
}: {
  title: string
  testId: string
  limit: DrivingLimit
  period: Period
  children?: ReactNode
}) => {
  const { t } = useTranslation()
  const limitDuration = formatDuration(t, limit.limitSeconds)
  return (
    <Section title={title} testId={testId}>
      <div className="flex items-end justify-between">
        {limit.doneSeconds === null ? (
          <Figure
            value="—"
            label={
              period === "week"
                ? t("reports.workTime.doneNotReported")
                : t("reports.workTime.drivenNotReported")
            }
          />
        ) : (
          <Figure
            value={formatDuration(t, limit.doneSeconds)}
            label={
              period === "week"
                ? t("reports.workTime.done")
                : t("reports.workTime.driven")
            }
          />
        )}
        <Figure
          value={
            limit.leftSeconds === null
              ? "—"
              : formatDuration(t, limit.leftSeconds)
          }
          label={t("reports.workTime.leftOf", { duration: limitDuration })}
          align="right"
        />
      </div>
      <Meter
        share={limit.usedShare}
        caption={
          period === "week"
            ? t("reports.workTime.ofThisWeek", { duration: limitDuration })
            : t("reports.workTime.ofTwoWeeks", { duration: limitDuration })
        }
      />
      {children}
    </Section>
  )
}

const isKnown = (limit: DrivingLimit) =>
  limit.doneSeconds !== null || limit.leftSeconds !== null

const extensionsLine = (t: TFunction, left: number) =>
  left === 0
    ? t("reports.workTime.noExtensionsLeft")
    : t("reports.workTime.extensionsLeft", { count: left })

const TodaySection = ({
  today,
  resting,
  doubleCrew,
}: {
  today: TodayFigures
  resting: Resting | null
  doubleCrew: boolean
}) => {
  const { t } = useTranslation()
  const { nextBreak } = today
  const breakLine =
    nextBreak &&
    (nextBreak.endsAt
      ? t("reports.workTime.breakDueUntil", {
          start: nextBreak.startsAt.label,
          end: nextBreak.endsAt.label,
        })
      : t("reports.workTime.breakDue", { start: nextBreak.startsAt.label }))
  return (
    <Section
      title={t("common.today")}
      testId="work-time-today"
      aside={
        doubleCrew ? (
          <Badge variant="info">{t("reports.workTime.doubleCrew")}</Badge>
        ) : null
      }
    >
      <div className="flex items-end justify-between">
        <Figure
          value={formatDuration(t, today.doneSeconds)}
          label={t("reports.workTime.done")}
        />
        <Figure
          value={formatDuration(t, today.leftSeconds)}
          label={t("reports.workTime.left")}
          align="right"
        />
      </div>
      <Meter
        share={today.usedShare}
        caption={t("reports.workTime.ofAvailableToday", {
          duration: formatDuration(t, today.availableSeconds),
        })}
        marker={
          nextBreak && breakLine
            ? { share: nextBreak.position, label: breakLine }
            : undefined
        }
      />
      <div className="flex flex-col gap-1">
        {resting && (
          <InfoRow>
            <Trans
              i18nKey="reports.workTime.restingSince"
              values={{
                since: resting.since.label,
                duration: formatDuration(t, resting.seconds),
              }}
              components={{ b: <Emphasis /> }}
            />
          </InfoRow>
        )}
        {breakLine && <InfoRow tone="warning">{breakLine}</InfoRow>}
        {today.dayEndsAt && (
          <InfoRow>
            <Trans
              i18nKey="reports.workTime.workDayEndsAt"
              values={{ time: today.dayEndsAt.label }}
              components={{ b: <Emphasis /> }}
            />
          </InfoRow>
        )}
        {today.dailyExtensionsLeft !== null && (
          <InfoRow>{extensionsLine(t, today.dailyExtensionsLeft)}</InfoRow>
        )}
      </div>
    </Section>
  )
}

const WeeklyRestRow = ({ rest }: { rest: WeeklyRest }) => {
  const { t } = useTranslation()
  if (rest.inProgress)
    return <InfoRow>{t("reports.workTime.weeklyRestInProgress")}</InfoRow>
  const length =
    rest.requiredSeconds === null
      ? null
      : formatDuration(t, rest.requiredSeconds)
  if (rest.startsBy === null)
    return length ? (
      <InfoRow>
        <Trans
          i18nKey="reports.workTime.nextWeeklyRest"
          values={{ length }}
          components={{ b: <Emphasis /> }}
        />
      </InfoRow>
    ) : null
  return (
    <InfoRow>
      {length ? (
        <Trans
          i18nKey="reports.workTime.nextWeeklyRestByLong"
          values={{ time: rest.startsBy.label, length }}
          components={{ b: <Emphasis /> }}
        />
      ) : (
        <Trans
          i18nKey="reports.workTime.nextWeeklyRestBy"
          values={{ time: rest.startsBy.label }}
          components={{ b: <Emphasis /> }}
        />
      )}
    </InfoRow>
  )
}

const DriverHeader = ({ workTime }: { workTime: DriverWorkTime }) => {
  const { t } = useTranslation()
  const { driverName, freshness } = workTime
  if (!driverName && !freshness) return null
  return (
    <div className="flex items-baseline justify-between gap-2 text-xs">
      <span className="truncate font-medium text-foreground">{driverName}</span>
      {freshness && (
        <span
          className={cn(
            "shrink-0",
            freshness.stale
              ? "font-medium text-amber-700 dark:text-amber-400"
              : "text-muted-foreground",
          )}
        >
          {freshness.stale
            ? t("reports.workTime.tachoReadAtAgo", {
                time: freshness.at.label,
                ago: formatDuration(t, freshness.ageSeconds),
              })
            : t("reports.workTime.tachoReadAt", { time: freshness.at.label })}
        </span>
      )}
    </div>
  )
}

const WorkTimeCard = ({ workTime }: { workTime: DriverWorkTime }) => {
  const { t } = useTranslation()
  const stale = workTime.freshness?.stale ?? false
  const showWeek = isKnown(workTime.week) || workTime.week.weeklyRest !== null
  return (
    <div
      data-testid="work-time-driver"
      data-stale={stale}
      className="flex flex-col gap-3 p-4"
    >
      <DriverHeader workTime={workTime} />
      <div className={cn("flex flex-col gap-3", stale && "opacity-50")}>
        <TodaySection
          today={workTime.today}
          resting={workTime.resting}
          doubleCrew={workTime.doubleCrew}
        />
        {showWeek && (
          <LimitSection
            title={t("reports.workTime.thisWeek")}
            testId="work-time-week"
            limit={workTime.week}
            period="week"
          >
            {workTime.week.weeklyRest && (
              <WeeklyRestRow rest={workTime.week.weeklyRest} />
            )}
          </LimitSection>
        )}
        {isKnown(workTime.twoWeeks) && (
          <LimitSection
            title={t("reports.workTime.twoWeeks")}
            testId="work-time-two-weeks"
            limit={workTime.twoWeeks}
            period="twoWeeks"
          />
        )}
      </div>
    </div>
  )
}

export const WorkTimePanel = ({
  workTimes,
}: {
  workTimes: DriverWorkTime[]
}) => {
  const { t } = useTranslation()
  return workTimes.length === 0 ? (
    <div
      data-testid="work-time-empty"
      className="flex h-full flex-col items-center justify-center gap-2 p-6 text-center"
    >
      <Clock className="h-8 w-8 text-muted-foreground" />
      <p className="text-sm text-muted-foreground">
        {t("reports.workTime.empty")}
      </p>
    </div>
  ) : (
    <div className="h-full divide-y divide-border overflow-y-auto bg-card">
      {workTimes.map((workTime) => (
        <WorkTimeCard key={workTime.key} workTime={workTime} />
      ))}
    </div>
  )
}
